/// <amd-module name="Search/Shared/Base/Telemetry" />
import * as CommonCIConstants from "Search/Shared/CustomerIntelligenceConstants";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IVssPageContext } from "VSS/Platform/Context";

/**
 * Writes telemetry events for code search page.
 */
export interface ITelemetryWriter {
    publish: (feature: string, extraProperties: { [key: string]: any }) => void;

    dispose: () => void;
}

export interface SearchPerfTelemetryAdditionalData {
    responseActivityId: string;

    searchSource?: string;
}

export function getTimestamp(): number {
    return Date.now();
}

/**
 * Telemetry writer to log telemetry events for Async network calls.
 */
export class SearchQueryResultsTelemetryWriter {
    private pendingRequestsStartTime: { [key: number]: number };
    private nextRequestId: number;

    constructor(private telemetryName: string, private entity: string, private pageContext: IVssPageContext) {
        this.pendingRequestsStartTime = {};
        this.nextRequestId = 0;
    }

    /**
    * Call this method before making the API call to be measured.
    * Returns the id of the request the class tracks for further action e.g. completion.
    */
    public notifyStarted = (): number => {
        this.nextRequestId += 1;
        this.pendingRequestsStartTime[this.nextRequestId] = Date.now();

        return this.nextRequestId;
    }

    /**
    * On accounts of API failures remove from tracking requests.
    */
    public notifyFailed = (requestId: number): void => {
        if (typeof this.pendingRequestsStartTime[requestId] !== "undefined") {
            delete this.pendingRequestsStartTime[requestId];
        }
    }

    /**
    * On successfull compeletion stop tracking the request and log the telemetry with time it took for the request to complete.
    * Also logs the additional data that is passed as part of the same event.
    */
    public notifySucceeded = (requestId: number, data: SearchPerfTelemetryAdditionalData): void => {
        if (typeof this.pendingRequestsStartTime[requestId] !== "undefined") {
            publishTelemetryEvent(
                this.pageContext,
                this.telemetryName,
                this.entity,
                {
                    ...data,
                    TotalTime: Date.now() - this.pendingRequestsStartTime[requestId]
                });

            delete this.pendingRequestsStartTime[requestId];
        }
    }
}

export function publishTelemetryEvent(pageContext: IVssPageContext, telemetryName: string, entity: string, additionalProps: { [key: string]: any }): void {
    const telemetryService: IVssTelemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    telemetryService.publishEvent(CommonCIConstants.SEARCH_AREA, telemetryName, { ...additionalProps, entity });
}